import React from 'react';
import { DocPageLayout } from '../components/DocPageLayout';
import { InfoBox } from '../components/InfoBox';
import { CodeBlock } from '../components/CodeBlock';
import { StepList } from '../components/StepList';
export function BatchIntegrationPage() {
  const steps = [{
    title: 'Prepare your batch file',
    content: <p data-id="element-1051">
          Create a JSON file containing an array of operations. Each operation
          must include the transaction type, order details, and payment
          information.
        </p>
  }, {
    title: 'Upload the batch file',
    content: <>
          <p data-id="element-1052">Submit the batch file to the gateway API using a POST request.</p>
          <CodeBlock language="cURL" title="Upload Batch" code={`curl https://test-gateway.mastercard.com/api/rest/version/72/merchant/{merchantId}/batch/{batchId} \\
  -u "merchant.{merchantId}:{apiPassword}" \\
  -X PUT \\
  -H "Content-Type: application/json" \\
  -d @batch-file.json`} data-id="element-1053" />
        </>
  }, {
    title: 'Monitor processing status',
    content: <p data-id="element-1054">
          Poll the batch status endpoint to track progress. The gateway
          processes each operation sequentially and updates the status in
          real-time.
        </p>
  }, {
    title: 'Retrieve results',
    content: <p data-id="element-1055">
          Once processing is complete, download the results file containing the
          outcome of each individual operation in the batch.
        </p>
  }];
  return <DocPageLayout title="Hosted <<batchIntegration>>" description="Process multiple operations securely and efficiently using batch file uploads." data-id="element-1056">
      <p className="text-gray-700 mb-6" data-id="element-1057">
        Batch Integration allows you to submit multiple payment operations in a
        single file for processing. This is ideal for merchants who need to
        process bulk refunds, recurring billing, or large volumes of
        transactions efficiently.
      </p>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1058">Use Cases</h2>
      <ul className="list-disc list-inside space-y-2 text-gray-700 mb-8 ml-4" data-id="element-1059">
        <li data-id="element-1060">
          <strong data-id="element-1061">Bulk refunds</strong> — Process multiple refund requests at
          once
        </li>
        <li data-id="element-1062">
          <strong data-id="element-1063">Recurring billing</strong> — Charge subscription customers in
          batches
        </li>
        <li data-id="element-1064">
          <strong data-id="element-1065">Settlement</strong> — Capture multiple authorized transactions
          simultaneously
        </li>
        <li data-id="element-1066">
          <strong data-id="element-1067">Payouts</strong> — Distribute funds to multiple recipients
        </li>
      </ul>

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1068">
        Processing Workflow
      </h2>
      <StepList steps={steps} data-id="element-1069" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1070">
        Batch File Format
      </h2>
      <CodeBlock language="JSON" title="Example Batch File" code={`{
  "apiOperation": "PAY",
  "transaction": [
    {
      "order": {
        "id": "BATCH-ORDER-001",
        "amount": "25.00",
        "currency": "USD"
      },
      "sourceOfFunds": {
        "token": "9111111111111111"
      }
    },
    {
      "order": {
        "id": "BATCH-ORDER-002",
        "amount": "50.00",
        "currency": "USD"
      },
      "sourceOfFunds": {
        "token": "9222222222222222"
      }
    }
  ]
}`} data-id="element-1071" />

      <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4" data-id="element-1072">
        Supported Operations
      </h2>
      <div className="overflow-x-auto my-6" data-id="element-1073">
        <table className="w-full text-sm border border-gray-200 rounded-lg overflow-hidden" data-id="element-1074">
          <thead data-id="element-1075">
            <tr className="bg-gray-50" data-id="element-1076">
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1077">
                Operation
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1078">
                Description
              </th>
              <th className="text-left px-4 py-3 font-semibold text-gray-900 border-b" data-id="element-1079">
                Requires Token
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100" data-id="element-1080">
            <tr data-id="element-1081">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1082">PAY</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1083">
                Authorize and capture in one step
              </td>
              <td className="px-4 py-3" data-id="element-1084">Yes</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1085">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1086">CAPTURE</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1087">
                Capture a previously authorized transaction
              </td>
              <td className="px-4 py-3" data-id="element-1088">No</td>
            </tr>
            <tr data-id="element-1089">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1090">REFUND</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1091">
                Refund a captured transaction
              </td>
              <td className="px-4 py-3" data-id="element-1092">No</td>
            </tr>
            <tr className="bg-gray-50/50" data-id="element-1093">
              <td className="px-4 py-3 font-mono text-xs" data-id="element-1094">VOID</td>
              <td className="px-4 py-3 text-gray-600" data-id="element-1095">
                Cancel an authorized transaction
              </td>
              <td className="px-4 py-3" data-id="element-1096">No</td>
            </tr>
          </tbody>
        </table>
      </div>

      <InfoBox type="info" title="Batch Limits" data-id="element-1097">
        Each batch can contain up to 1,000 operations. For larger volumes, split
        your operations across multiple batch files and submit them
        sequentially.
      </InfoBox>
    </DocPageLayout>;
}